import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import {
  PatientSuccessModalProps,
  PATIENT_MODAL_TEXTS,
  PATIENT_MODAL_STYLES,
} from "../../../utils/constants";

const PatientSuccessModal: React.FC<PatientSuccessModalProps> = ({
  open,
  onClose,
  patientName,
  patientEmail,
  onCreateAnother,
}) => {
  const handleCreateAnother = () => {
    onClose();
    if (onCreateAnother) {
      onCreateAnother();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: "white",
        },
      }}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          width: 500,
          maxWidth: "90vw",
          minHeight: 350,
        },
        "& .MuiBackdrop-root": {
          backgroundColor: PATIENT_MODAL_STYLES.overlayColor,
        },
      }}
    >
      {/* Título del Modal */}
      <DialogTitle
        sx={{
          textAlign: "center",
          padding: "20px 24px 16px",
          position: "relative",
        }}
      >
        {/* Botón de cerrar */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Contenido del Modal */}
      <DialogContent sx={{ padding: "0 24px", textAlign: "center" }}>
        {/* Checkmark Verde */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: PATIENT_MODAL_STYLES.successColor,
            }}
          />
        </Box>

        {/* Título de Éxito */}
        <Typography
          variant="h6"
          component="h2"
          fontWeight={600}
          sx={{ mb: 2, color: (theme) => theme.palette.text.primary }}
        >
          {PATIENT_MODAL_TEXTS.SUCCESS_TITLE}
        </Typography>

        {/* Mensaje de Confirmación */}
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            color: (theme) => theme.palette.text.secondary,
            lineHeight: 1.5,
          }}
        >
          {PATIENT_MODAL_TEXTS.SUCCESS_MESSAGE}
        </Typography>

        {/* Información del Paciente Creado */}
        {(patientName || patientEmail) && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: (theme) => theme.palette.grey[50],
              borderRadius: 1,
              border: `1px solid ${(theme: any) => theme.palette.grey[200]}`,
            }}
          >
            {patientName && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.primary,
                  mb: 1,
                }}
              >
                Paciente: {patientName}
              </Typography>
            )}
            {patientEmail && (
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                }}
              >
                Email: {patientEmail}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Botones de Acción */}
      <DialogActions
        sx={{
          padding: "16px 24px 24px",
          gap: 2,
          justifyContent: "center",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        {/* Botón Crear Otro Paciente (opcional) */}
        {onCreateAnother && (
          <Button
            onClick={handleCreateAnother}
            variant="outlined"
            sx={{
              borderColor: PATIENT_MODAL_STYLES.primaryButtonColor,
              color: PATIENT_MODAL_STYLES.primaryButtonColor,
              "&:hover": {
                borderColor: "#c8d661",
                backgroundColor: "rgba(218, 232, 109, 0.1)",
              },
              minWidth: 160,
              order: { xs: 2, sm: 1 },
            }}
          >
            {PATIENT_MODAL_TEXTS.CREATE_ANOTHER_BUTTON}
          </Button>
        )}

        {/* Botón Aceptar */}
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: PATIENT_MODAL_STYLES.primaryButtonColor,
            color: "#333",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#c8d661",
            },
            minWidth: 120,
            order: { xs: 1, sm: 2 },
          }}
        >
          {PATIENT_MODAL_TEXTS.SUCCESS_BUTTON}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientSuccessModal;
